
import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Loader2, Upload, Copy, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { voicePresets } from '@/utils/voicePresets';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Info, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface VoiceConfigFormData {
  pages: { name: string; path: string }[];
  customCommands: string;
  customResponses: string;
}

interface VoiceAssistantConfigProps {
  installationKey?: string;
}

const VoiceAssistantConfig = ({ installationKey }: VoiceAssistantConfigProps = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [configScript, setConfigScript] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('full');
  const { toast } = useToast();
  
  const form = useForm<VoiceConfigFormData>({
    defaultValues: {
      pages: [
        { name: 'home', path: '/' },
        { name: 'about', path: '/about' },
        { name: 'contact', path: '/contact' }
      ],
      customCommands: '{\n  "open menu": "toggleMenu",\n  "close panel": "closePanel"\n}',
      customResponses: '{\n  "help": "I can help you navigate the website. Try saying \'go to\' followed by a page name",\n  "notFound": "I couldn\'t find that page. Try asking for help."\n}'
    }
  });

  // Use fieldArray to manage dynamic page inputs
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "pages"
  });

  const handlePresetChange = (presetId: string) => {
    const preset = voicePresets.find(p => p.id === presetId);
    if (preset) {
      form.setValue('customCommands', preset.config.customCommands);
      form.setValue('customResponses', preset.config.customResponses);
      toast({ title: `Applied ${preset.name} configuration`, variant: "default" });
    }
  };

  // Parse JSON safely with better error handling
  const parseJsonSafely = (jsonString: string, fieldName: string): { data: any, error: string | null } => {
    try {
      return { data: JSON.parse(jsonString), error: null };
    } catch (e: any) {
      const errorMessage = `${fieldName} error: ${(e as Error).message}`;
      return { data: null, error: errorMessage };
    }
  };

  // Generate configuration with user-provided routes
  const generateConfiguration = (): { fullScript: string, configOnly: string, debug: boolean } | null => {
    // Parse custom commands
    const { data: commands, error: commandsError } = parseJsonSafely(form.getValues('customCommands'), 'Custom Commands');
    if (commandsError) {
      setErrorDetail(commandsError);
      toast({ title: 'Custom Commands is not valid JSON.', variant: "destructive" });
      return null;
    }

    // Parse custom responses
    const { data: customResponses, error: responsesError } = parseJsonSafely(form.getValues('customResponses'), 'Custom Responses');
    if (responsesError) {
      setErrorDetail(responsesError);
      toast({ title: 'Custom Responses is not valid JSON.', variant: "destructive" });
      return null;
    }

    // Create routes object from form data
    const routes: Record<string, string> = {};
    form.getValues('pages').forEach(page => {
      if (page.name && page.path) {
        routes[page.name] = page.path;
      }
    });

    // Debug mode enabled by default for easier troubleshooting
    const debug = true;
    
    // Just the configuration block
    const configOnly = `<!-- Accesstopia Widget Configuration -->
<script>
  window.ACCESSTOPIA_CONFIG = {
    routes: ${JSON.stringify(routes, null, 2)},
    commands: ${JSON.stringify(commands, null, 2)},
    customResponses: ${JSON.stringify(customResponses, null, 2)},
    debug: ${debug}, // Set to false in production
    // You may add/override additional options here:
    // buttonPosition: 'bottom-left', // Options: bottom-left, bottom-right, top-left, top-right
    // voiceAssistantPosition: 'bottom-center', // Options: bottom-center, bottom-left, bottom-right
    // defaultDarkMode: false,
    // defaultFontSize: 100,
    // primaryColor: '#0066cc',
    // enableVoiceAssistant: true
  };
</script>`;

    // Full installation script with both stylesheet and bootstrap loader
    const fullScript = `<!-- Add to your HTML <head> -->
<link 
  rel="stylesheet" 
  href="https://lmniterbzhfihvxsumbl.supabase.co/functions/v1/widget/styles?v=1.0.3"
/>

<!-- Add before </body> closing tag -->
${configOnly}

<!-- Accesstopia Bootstrap Loader -->
<script type="text/javascript">
  (function() {
    if (window.AccesstopiaWidget) {
      console.warn('Accesstopia Widget already initialized');
      return;
    }
    var s = document.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    s.src = 'https://lmniterbzhfihvxsumbl.supabase.co/functions/v1/widget?key=${installationKey || '{YOUR_INSTALLATION_KEY}'}&v=1.0.3';
    s.onload = function() {
      console.log('Accesstopia: Widget loaded successfully');
    };
    s.onerror = function(e) { 
      console.error('Accesstopia: Failed to load widget:', e);
    };
    var x = document.getElementsByTagName('script')[0];
    x.parentNode.insertBefore(s, x);
  })();
</script>`;

    return { fullScript, configOnly, debug };
  };

  const onSubmit = async (data: VoiceConfigFormData) => {
    setIsLoading(true);
    setErrorDetail(null);
    setConfigScript(null);
    
    try {
      // Check if JSON fields are valid before proceeding
      const { error: commandsError } = parseJsonSafely(data.customCommands, 'Custom Commands');
      const { error: responsesError } = parseJsonSafely(data.customResponses, 'Custom Responses');
      
      if (commandsError) {
        setErrorDetail(commandsError);
        toast({ title: 'Custom Commands is not valid JSON.', description: commandsError, variant: "destructive" });
        return;
      }
      
      if (responsesError) {
        setErrorDetail(responsesError);
        toast({ title: 'Custom Responses is not valid JSON.', description: responsesError, variant: "destructive" });
        return;
      }

      const config = generateConfiguration();
      if (config) {
        setConfigScript(activeTab === 'full' ? config.fullScript : config.configOnly);
        toast({ title: 'Configuration generated successfully!', variant: "default" });
        try {
          await navigator.clipboard.writeText(activeTab === 'full' ? config.fullScript : config.configOnly);
          toast({ title: 'Configuration copied to clipboard!', variant: "default" });
        } catch {
          toast({
            title: "Failed to copy configuration script.",
            description: "Please copy manually from the generated script below.",
            variant: "destructive"
          });
        }
      }
    } catch (error: any) {
      setErrorDetail(error.message);
      toast({
        title: "Error generating configuration",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPage = () => {
    append({ name: '', path: '' });
  };

  const handleCopy = async () => {
    if (configScript) {
      try {
        await navigator.clipboard.writeText(configScript);
        toast({ title: "Copied to clipboard!", variant: "default" });
      } catch {
        toast({ title: "Failed to copy.", variant: "destructive" });
      }
    }
  };

  return (
    <div className="bg-muted/50 rounded-lg p-4 md:p-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            <FormItem>
              <FormLabel>Configuration Preset</FormLabel>
              <Select onValueChange={handlePresetChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a preset configuration" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {voicePresets.map((preset) => (
                    <SelectItem key={preset.id} value={preset.id}>
                      {preset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                Choose a preset configuration or customize the settings below
              </FormDescription>
            </FormItem>
          </div>

          <Card className="p-4 border-dashed">
            <h3 className="font-medium text-sm mb-3">Page Routes Configuration</h3>
            <div className="space-y-4">
              {fields.map((field, index) => (
                <div key={field.id} className="flex gap-3 items-start">
                  <FormField
                    control={form.control}
                    name={`pages.${index}.name`}
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel className={index !== 0 ? "sr-only" : undefined}>
                          {index === 0 ? "Page Name" : ""}
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="Page name (e.g., home)" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name={`pages.${index}.path`}
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel className={index !== 0 ? "sr-only" : undefined}>
                          {index === 0 ? "URL Path" : ""}
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="URL path (e.g., /home)" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  
                  <div className={`mt-${index === 0 ? '7' : '0'}`}>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => remove(index)}
                      disabled={index < 3}
                      className="h-10 w-10"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Remove page</span>
                    </Button>
                  </div>
                </div>
              ))}
              
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddPage}
                className="mt-2"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Page
              </Button>
              
              <FormDescription>
                Define the pages and URL paths for voice navigation
              </FormDescription>
            </div>
          </Card>

          <FormField
            control={form.control}
            name="customCommands"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Custom Commands (JSON)</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Enter custom commands as JSON"
                    className="font-mono text-sm"
                    rows={4}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Define custom voice commands and their corresponding actions
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="customResponses"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Custom Responses (JSON)</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Enter custom responses as JSON"
                    className="font-mono text-sm"
                    rows={4}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Customize the voice assistant's responses for different scenarios
                </FormDescription>
              </FormItem>
            )}
          />

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="full">Complete Installation</TabsTrigger>
              <TabsTrigger value="config">Configuration Only</TabsTrigger>
            </TabsList>
            
            <TabsContent value="full" className="mt-2">
              <Alert variant="default" className="mb-2">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Full installation script with both stylesheet and bootstrap loader.
                </AlertDescription>
              </Alert>
            </TabsContent>
            
            <TabsContent value="config" className="mt-2">
              <Alert variant="default" className="mb-2">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Configuration block only (for use with existing installations).
                </AlertDescription>
              </Alert>
            </TabsContent>
          </Tabs>

          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Upload className="mr-2 h-4 w-4" />
            Generate Configuration
          </Button>

          {errorDetail && (
            <div className="bg-destructive/10 border border-destructive text-destructive rounded p-3 mt-4 text-sm">
              <strong>Error:</strong> {errorDetail}
            </div>
          )}

          {configScript && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-base">Generated Configuration</span>
                <Button size="sm" variant="secondary" onClick={handleCopy} type="button">
                  <Copy className="mr-1 h-4 w-4" />
                  Copy
                </Button>
              </div>
              
              <Alert variant="destructive" className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>IMPORTANT:</strong> Only include ONE configuration block in your entire website!
                  Including multiple instances will cause conflicts.
                </AlertDescription>
              </Alert>
              
              <Textarea
                value={configScript}
                readOnly
                rows={10}
                className="font-mono text-xs bg-muted/30 border p-2"
                style={{ minHeight: 180, resize: 'vertical' }}
              />
              
              <div className="space-y-3 mt-4">
                <h4 className="font-medium">Installation Instructions</h4>
                <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-2">
                  {activeTab === 'full' ? (
                    <>
                      <li><strong>Stylesheet:</strong> Add the link tag to your HTML <code>&lt;head&gt;</code> section.</li>
                      <li><strong>Configuration:</strong> Add the configuration script before the closing <code>&lt;/body&gt;</code> tag.</li>
                      <li><strong>Bootstrap Loader:</strong> Add the bootstrap loader script right after the configuration script.</li>
                    </>
                  ) : (
                    <>
                      <li>Add this configuration script before the closing <code>&lt;/body&gt;</code> tag.</li>
                      <li>This should be placed <strong>before</strong> the Accesstopia widget script.</li>
                    </>
                  )}
                </ul>
                
                <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/50 rounded-lg p-3">
                  <h5 className="font-medium text-amber-800 dark:text-amber-300 flex items-center text-sm">
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Troubleshooting
                  </h5>
                  <ul className="text-xs pl-5 mt-1 list-disc text-amber-700 dark:text-amber-400">
                    <li>Make sure to include <strong>both</strong> the stylesheet AND scripts when using the full installation.</li>
                    <li>The configuration block must come <strong>before</strong> the bootstrap loader.</li>
                    <li>Enable debug mode by setting <code>debug: true</code> in the configuration.</li>
                    <li>Check browser console for detailed error messages.</li>
                  </ul>
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground mt-3">
                {activeTab === 'config' ? (
                  <>
                    This configuration block should be added <strong>before</strong> the widget script. Don't forget to include the stylesheet in your page's <code>&lt;head&gt;</code>.
                  </>
                ) : (
                  <>
                    Make sure to replace <code>{'{YOUR_INSTALLATION_KEY}'}</code> with your actual installation key if not automatically filled.
                  </>
                )}
              </p>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
};

export default VoiceAssistantConfig;
